
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsxm from '@/lib/clsxm';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import Seo from '@/components/Seo';
import { music_subpages, MusicSubpage, MusicSubpageCategory, } from '@/constants/music_genres';
import { useUser } from '@/utils/useUser';
const Footer = dynamic(() => import('@/components/layout/Footer'));
interface Props {
    subpage: MusicSubpage;
}
const categoryLabel: Record<MusicSubpageCategory, string> = {
    genre: 'Genre',
    'use-case': 'Use Case',
    mood: 'Mood',
};
const iconMap: Record<string, React.ReactNode> = {
    guitar: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>),
    microphone: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z'/>),
    headphones: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M15.536 8.464a5 5 0 010 7.072M12 12h.01M8.464 8.464a5 5 0 000 7.072M6.343 6.343a8 8 0 000 11.314M17.657 6.343a8 8 0 010 11.314'/>),
    saxophone: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>),
    piano: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>),
    synth: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4'/>),
    vinyl: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>),
    banjo: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>),
    waves: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M13 10V3L4 14h7v7l9-11h-7z'/>),
    heart: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'/>),
    lightning: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M13 10V3L4 14h7v7l9-11h-7z'/>),
    acoustic: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>),
    maracas: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3'/>),
    star: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z'/>),
    sun: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z'/>),
    video: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'/>),
    gamepad: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z'/>),
    mic: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z'/>),
    lotus: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'/>),
    fire: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z'/>),
    book: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'/>),
    film: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z'/>),
    cloud: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z'/>),
    crown: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M5 3l3.057-3L12 4.5 15.943 0 19 3l2 13H3L5 3zM3 20h18v2H3v-2z'/>),
    droplet: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 21a8 8 0 004-14.952L12 2l-4 4.048A8 8 0 0012 21z'/>),
    sparkle: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z'/>),
    bolt: (<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M13 10V3L4 14h7v7l9-11h-7z'/>),
};
function getIcon(iconName: string) {
    return (<svg className='h-7 w-7 text-[var(--music-gold)]' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
      {iconMap[iconName] || iconMap['guitar']}
    </svg>);
}
function DemoTrackPlayer({ demoTrack, }: {
    demoTrack: NonNullable<MusicSubpage['demoTrack']>;
}) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState('0:00');
    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        if (isPlaying) {
            audio.pause();
        }
        else {
            audio.play().catch(() => { });
        }
    }, [isPlaying]);
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onEnded = () => {
            setIsPlaying(false);
            setProgress(0);
            setCurrentTime('0:00');
        };
        const onTimeUpdate = () => {
            if (audio.duration) {
                setProgress((audio.currentTime / audio.duration) * 100);
                const m = Math.floor(audio.currentTime / 60);
                const s = Math.floor(audio.currentTime % 60);
                setCurrentTime(`${m}:${s.toString().padStart(2, '0')}`);
            }
        };
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('timeupdate', onTimeUpdate);
        return () => {
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('timeupdate', onTimeUpdate);
        };
    }, []);
    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || !audio.duration)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const pct = x / rect.width;
        audio.currentTime = pct * audio.duration;
    };
    return (<div className='relative'>
      <audio ref={audioRef} src={demoTrack.audioUrl} preload='metadata'/>

      <div className='flex items-center gap-5'>
        {/* Play/Pause button */}
        <button onClick={togglePlay} className='flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[var(--music-gold)] text-[var(--music-bg-deep)] shadow-lg transition-all hover:bg-[var(--music-gold-light)] hover:scale-105'>
          {isPlaying ? (<svg className='h-6 w-6' fill='currentColor' viewBox='0 0 24 24'>
              <rect x='6' y='4' width='4' height='16' rx='1'/>
              <rect x='14' y='4' width='4' height='16' rx='1'/>
            </svg>) : (<svg className='h-6 w-6' fill='currentColor' viewBox='0 0 24 24'>
              <path d='M8 5v14l11-7z'/>
            </svg>)}
        </button>

        {/* Track info + progress */}
        <div className='min-w-0 flex-1'>
          <div className='mb-1 flex items-center justify-between gap-3'>
            <h3 className='truncate text-lg font-semibold text-[var(--music-cream)]'>
              {demoTrack.name}
            </h3>
            <span className='flex-shrink-0 text-sm text-[var(--music-gray)]'>
              {currentTime} / {demoTrack.duration}
            </span>
          </div>

          {/* Progress bar */}
          <div className='group relative h-2 cursor-pointer rounded-full bg-white/10' onClick={handleProgressClick}>
            <div className='absolute inset-y-0 left-0 rounded-full bg-[var(--music-gold)] transition-all' style={{ width: `${progress}%` }}/>
            <div className='absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full bg-[var(--music-gold)] opacity-0 shadow transition-opacity group-hover:opacity-100' style={{ left: `${progress}%`, marginLeft: '-7px' }}/>
          </div>

          <p className='mt-2 text-sm italic text-[var(--music-gray-dark)] line-clamp-2'>
            &ldquo;{demoTrack.prompt}&rdquo;
          </p>
        </div>
      </div>
    </div>);
}
export default function MusicSubpagePage({ subpage }: Props) {
    const { t } = useLocale('music-generator');
    const { user } = useUser();
    const relatedPages = music_subpages.filter((s) => subpage.relatedSlugs.includes(s.slug));
    // Build FAQ structured data
    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: subpage.faqs.map((faq) => ({
            '@type': 'Question',
            name: t(faq.question),
            acceptedAnswer: {
                '@type': 'Answer',
                text: t(faq.answer),
            },
        })),
    };
    return (<>
      <Head>
        <link rel='preconnect' href='https://fonts.googleapis.com'/>
        <link rel='preconnect' href='https://fonts.gstatic.com' crossOrigin='anonymous'/>
        <link href='https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Inter:wght@300;400;500;600;700&display=swap' rel='stylesheet'/>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}/>
      </Head>

      <style jsx global>{`
        :root {
          --music-bg-deep: #0a0e27;
          --music-bg-card: #12152e;
          --music-bg-elevated: #1a1f3a;
          --music-gold: #f59e0b;
          --music-gold-light: #fbbf24;
          --music-gold-dark: #d97706;
          --music-cream: #f5f5f0;
          --music-gray: #9ca3af;
          --music-gray-dark: #6b7280;
        }

        .font-display {
          font-family: 'Cormorant Garamond', serif;
        }

        .font-body {
          font-family:
            'Inter',
            -apple-system,
            BlinkMacSystemFont,
            sans-serif;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
        }

        .delay-100 {
          animation-delay: 100ms;
        }

        .delay-200 {
          animation-delay: 200ms;
        }

        .delay-300 {
          animation-delay: 300ms;
        }
      `}</style>

      <div className='relative bg-[var(--music-bg-deep)] font-body'>
        <Seo templateTitle={t(subpage.seoTitle)} description={t(subpage.seoDescription)} image={subpage.demoTrack?.imageUrl ||
            `https://easy-peasy.ai/api/og-template?title=${encodeURIComponent(t(subpage.seoTitle))}`}/>
        <LandingPageNavigation />

        {/* Hero Section */}
        <section className='relative overflow-hidden px-6 pt-32 pb-20 lg:px-8 lg:pt-40'>
          <div className='absolute inset-0 overflow-hidden'>
            <div className='absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[var(--music-gold)] opacity-10 blur-3xl'></div>
            <div className='absolute top-60 -left-40 h-96 w-96 rounded-full bg-purple-600 opacity-10 blur-3xl'></div>
          </div>

          <div className='relative mx-auto max-w-7xl'>
            {/* Breadcrumb */}
            <div className='mb-8 flex items-center justify-center gap-2 text-sm text-[var(--music-gray)]'>
              <Link href='/ai-music-generator' className='transition-colors hover:text-[var(--music-gold)]'>
                AI Music Generator
              </Link>
              <span>/</span>
              <span className='text-[var(--music-cream)]'>
                {t(subpage.name)}
              </span>
            </div>

            <div className='text-center opacity-0 animate-fade-in-up'>
              <div className='mb-6 inline-block'>
                <span className='rounded-full border border-[var(--music-gold)]/30 bg-[var(--music-gold)]/10 px-4 py-1.5 text-sm font-medium text-[var(--music-gold)]'>
                  {categoryLabel[subpage.category]}
                </span>
              </div>
              <h1 className='font-display mb-6 text-5xl font-light leading-tight tracking-tight text-[var(--music-cream)] lg:text-7xl'>
                {t(subpage.heroHeading)}
              </h1>
              <p className='mx-auto max-w-3xl text-xl leading-relaxed text-[var(--music-gray)] lg:text-2xl'>
                {t(subpage.heroSubheading)}
              </p>

              <div className='mt-10'>
                <ButtonLink href='/music-generator' className='inline-flex items-center gap-2 rounded-full border-transparent bg-[var(--music-gold)] px-8 py-4 text-lg font-semibold text-[var(--music-bg-deep)] shadow-lg transition-all hover:bg-[var(--music-gold-light)] hover:shadow-xl hover:scale-105'>
                  {user ? t('Create Music') : t('Start Creating for Free')}
                  <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6'/>
                  </svg>
                </ButtonLink>
                {!user && (<p className='mt-4 text-sm text-[var(--music-gray)]'>
                    No credit card required
                  </p>)}
              </div>
            </div>
          </div>
        </section>

        {/* Description Section */}
        <section className='relative px-6 py-16 lg:px-8 lg:py-24'>
          <div className='mx-auto max-w-4xl'>
            <div className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-8 lg:p-12'>
              <div className='absolute -top-24 -right-24 h-48 w-48 rounded-full bg-[var(--music-gold)] opacity-5 blur-3xl'></div>
              <div className='relative flex flex-col gap-6 lg:flex-row lg:gap-10'>
                {subpage.demoTrack?.imageUrl && (<div className='w-full flex-shrink-0 overflow-hidden rounded-2xl lg:w-48'>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={subpage.demoTrack.imageUrl} alt={t(subpage.name)} className='h-auto w-full object-cover lg:h-48'/>
                  </div>)}
                <div className='min-w-0 flex-1'>
                  <p className='text-lg leading-relaxed text-[var(--music-gray)] lg:text-xl'>
                    {t(subpage.description)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Demo Track Player */}
        {subpage.demoTrack && (<section className='relative px-6 pb-8 lg:px-8 lg:pb-12'>
            <div className='mx-auto max-w-4xl'>
              <div className='mb-6 text-center'>
                <h2 className='font-display text-3xl font-light text-[var(--music-cream)] lg:text-4xl'>
                  Listen to a{' '}
                  <span className='font-semibold italic text-[var(--music-gold)]'>
                    sample
                  </span>
                </h2>
              </div>
              <DemoTrackPlayer key={subpage.slug} demoTrack={subpage.demoTrack}/>
            </div>
          </section>)}

        {/* Prompt Examples Section */}
        <section className='relative px-6 py-16 lg:px-8 lg:py-24'>
          <div className='mx-auto max-w-7xl'>
            <div className='mb-12 text-center'>
              <h2 className='font-display mb-4 text-4xl font-light text-[var(--music-cream)] lg:text-5xl'>
                Example{' '}
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  prompts
                </span>
              </h2>
              <p className='text-lg text-[var(--music-gray)]'>
                {t('Try these prompts to get started')}
              </p>
            </div>

            <div className='grid gap-6 lg:grid-cols-3'>
              {subpage.promptExamples.map((prompt, index) => (<div key={index} className='group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-6 transition-all hover:scale-[1.02]'>
                  <div className='absolute -top-12 -right-12 h-24 w-24 rounded-full bg-[var(--music-gold)] opacity-5 blur-2xl transition-all group-hover:opacity-10'></div>
                  <div className='relative'>
                    <div className='mb-3 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--music-gold)]/10 text-sm font-semibold text-[var(--music-gold)]'>
                      {index + 1}
                    </div>
                    <p className='text-sm leading-relaxed text-[var(--music-cream)]'>
                      &ldquo;{t(prompt)}&rdquo;
                    </p>
                  </div>
                </div>))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className='relative px-6 py-16 lg:px-8 lg:py-24'>
          <div className='absolute inset-0 bg-gradient-to-b from-transparent via-[var(--music-bg-card)]/50 to-transparent'></div>
          <div className='relative mx-auto max-w-7xl'>
            <div className='mb-12 text-center'>
              <h2 className='font-display mb-4 text-4xl font-light text-[var(--music-cream)] lg:text-5xl'>
                How it{' '}
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  works
                </span>
              </h2>
            </div>

            <div className='grid gap-8 lg:grid-cols-3'>
              {[
            {
                step: '1',
                title: t('Describe your music'),
                description: t('Write a text prompt describing the genre, mood, instruments, tempo, and style you want. Be as specific or creative as you like.'),
            },
            {
                step: '2',
                title: t('AI generates your track'),
                description: t('Our AI processes your description and composes a unique, professional-quality track in seconds. No musical knowledge required.'),
            },
            {
                step: '3',
                title: t('Download and use'),
                description: t('Preview your generated track, then download it in high-quality audio format. Use it in your projects royalty-free.'),
            },
        ].map((item) => (<div key={item.step} className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-8 text-center'>
                  <div className='mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--music-gold)] text-xl font-bold text-[var(--music-bg-deep)]'>
                    {item.step}
                  </div>
                  <h3 className='font-display mb-3 text-2xl font-semibold text-[var(--music-cream)]'>
                    {item.title}
                  </h3>
                  <p className='text-[var(--music-gray)]'>{item.description}</p>
                </div>))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className='relative px-6 py-16 lg:px-8 lg:py-24'>
          <div className='mx-auto max-w-3xl'>
            <div className='mb-12 text-center'>
              <h2 className='font-display mb-4 text-4xl font-light text-[var(--music-cream)] lg:text-5xl'>
                Frequently asked{' '}
                <span className='font-semibold italic text-[var(--music-gold)]'>
                  questions
                </span>
              </h2>
            </div>

            <div className='divide-y divide-white/10' itemScope itemType='https://schema.org/FAQPage'>
              {subpage.faqs.map((faq) => (<Disclosure as='div' key={faq.question} className='py-4' itemScope itemProp='mainEntity' itemType='https://schema.org/Question'>
                  {({ open }) => (<>
                      <Disclosure.Button className='flex w-full items-start justify-between text-left'>
                        <span className='text-lg font-medium text-[var(--music-cream)]' itemProp='name'>
                          {t(faq.question)}
                        </span>
                        <ChevronDownIcon className={clsxm(open ? '-rotate-180' : 'rotate-0', 'ml-4 h-5 w-5 flex-shrink-0 text-[var(--music-gold)] transition-transform')}/>
                      </Disclosure.Button>
                      <Disclosure.Panel as='div' className='mt-3 pr-12' itemScope itemProp='acceptedAnswer' unmount={false} itemType='https://schema.org/Answer'>
                        <p className='text-[var(--music-gray)]' itemProp='text'>
                          {t(faq.answer)}
                        </p>
                      </Disclosure.Panel>
                    </>)}
                </Disclosure>))}
            </div>
          </div>
        </section>

        {/* Related Genres/Use Cases Grid */}
        {relatedPages.length > 0 && (<section className='relative px-6 py-16 lg:px-8 lg:py-24'>
            <div className='absolute inset-0 bg-gradient-to-b from-transparent via-[var(--music-bg-card)]/50 to-transparent'></div>
            <div className='relative mx-auto max-w-7xl'>
              <div className='mb-12 text-center'>
                <h2 className='font-display mb-4 text-4xl font-light text-[var(--music-cream)] lg:text-5xl'>
                  Explore{' '}
                  <span className='font-semibold italic text-[var(--music-gold)]'>
                    more styles
                  </span>
                </h2>
              </div>

              <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                {relatedPages.map((page) => (<Link key={page.slug} href={`/ai-music-generator/${page.slug}`} className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--music-bg-card)] to-[var(--music-bg-elevated)] p-6 transition-all hover:scale-[1.02]`}>
                    <div className={`absolute inset-0 bg-gradient-to-br ${page.gradient} opacity-0 transition-opacity group-hover:opacity-100`}></div>
                    <div className='relative'>
                      <div className='mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--music-gold)]/10'>
                        {getIcon(page.icon)}
                      </div>
                      <h3 className='font-display text-xl font-semibold text-[var(--music-cream)]'>
                        {t(page.name)}
                      </h3>
                      <p className='mt-1 text-xs text-[var(--music-gray)]'>
                        {categoryLabel[page.category]}
                      </p>
                    </div>
                  </Link>))}
              </div>
            </div>
          </section>)}

        {/* Final CTA */}
        <section className='relative px-6 py-32 lg:px-8'>
          <div className='mx-auto max-w-4xl text-center'>
            <h2 className='font-display mb-6 text-5xl font-light text-[var(--music-cream)] lg:text-7xl'>
              Ready to create
              <br />
              <span className='font-semibold italic text-[var(--music-gold)]'>
                {t(subpage.name).toLowerCase()} music?
              </span>
            </h2>
            <p className='mb-10 text-xl text-[var(--music-gray)]'>
              {t('Join thousands of creators bringing their projects to life with AI-generated music')}
            </p>
            <div className='flex flex-col items-center gap-4 sm:flex-row sm:justify-center'>
              <ButtonLink href='/music-generator' className='inline-flex items-center gap-2 rounded-full border-transparent bg-[var(--music-gold)] px-8 py-4 text-lg font-semibold text-[var(--music-bg-deep)] shadow-lg transition-all hover:bg-[var(--music-gold-light)] hover:shadow-xl hover:scale-105'>
                {user ? t('Create Music') : t('Get Started for Free')}
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6'/>
                </svg>
              </ButtonLink>
              {!user && (<Link href='/login?preset=music' className='inline-flex items-center gap-2 text-lg font-medium text-[var(--music-gray)] transition-colors hover:text-[var(--music-cream)]'>
                  {t('Log in')}
                  <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 7l5 5m0 0l-5 5m5-5H6'/>
                  </svg>
                </Link>)}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>);
}
export async function getStaticPaths() {
    const locales = [
        'en',
        'ja',
        'es',
        'fr',
        'it',
        'de',
        'ko',
        'zh',
        'ar',
        'th',
        'pt',
        'ms',
    ];
    return {
        paths: locales.flatMap((locale) => music_subpages.map((subpage) => ({
            params: { slug: subpage.slug },
            locale,
        }))),
        fallback: false,
    };
}
const getStaticProps = async (ctx: GetStaticPropsContext) => {
    const slug = ctx.params?.slug as string;
    const subpage = music_subpages.find((s) => s.slug === slug);
    if (!subpage) {
        return {
            notFound: true,
        };
    }
    return {
        props: {
            subpage,
        },
    };
};

    async function __Next_Translate__getStaticProps__1a1052e9150__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-music-generator/[slug]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1052e9150__ as getStaticProps }
  